import React, { useEffect, useRef, useMemo } from "react";
import JoditEditor from "jodit-pro-react";
import "jodit/es2021/jodit.min.css";
import "./editor.css";
import {
  API_KEY_TRANSLATOR,
  LICENSE_KEY_EDITOR,
} from "../../../constants/utils.constants";

/**
 * Plugins Jodit PRO y dependencias externas:
 *
 * - Translate (Jodit Cloud): provider "cloud", clave en API_KEY_TRANSLATOR
 *   (REACT_APP_API_KEY_TRANSLATOR). El endpoint valida Referer: añadir el dominio
 *   en Cloud Settings → Referrers o las peticiones devuelven 403.
 *   https://xdsoft.net/jodit/pro/docs/cloud/translate.md
 *
 * - Google Maps: requiere Google Maps JavaScript API key (googleMaps.API_KEY).
 *   Habilitar: Maps JavaScript API, Geocoding API, Maps Static API, Places API.
 *   https://developers.google.com/maps/documentation/javascript/get-api-key
 *
 * - Google Search: requiere API key / Custom Search Engine (CSE) de Google.
 *
 * - Finder (filebrowser PRO): requiere backend con endpoints ajax (listar) y uploader (subir).
 *   Configurar filebrowser.ajax.url y filebrowser.uploader.url.
 *
 * - Export PDF/DOC (exportDocs): la generación puede requerir backend (p. ej. con dompdf).
 *
 * - AutoComplete: opcionalmente backend o lista de sugerencias personalizada.
 */

export default function Editor(props) {
  const { body, setBody, placeholder = "", className, lang = "en" } = props;
  const editorRef = useRef(null);

  // Configuración de Jodit
  const config = useMemo(() => {
    const translateEnabled = String(API_KEY_TRANSLATOR ?? "").trim().length > 0;

    // Lista única de botones para escritorio/tablet. Así evitamos que en tamaños
    // intermedios (MD/SM) JODIT use sus listas por defecto y aparezcan botones no definidos.
    const mainToolbarButtons = [
      "undo",
      "redo",
      "|",
      "cut",
      "copy",
      "paste",
      "|",
      "source",
      "|",
      "bold",
      "italic",
      "underline",
      "strikethrough",
      "|",
      "superscript",
      "subscript",
      "|",
      "font",
      "fontsize",
      "brush",
      "paragraph",
      "lineHeight",
      "|",
      "align",
      "|",
      "ul",
      "ol",
      "todoList",
      "|",
      "outdent",
      "indent",
      "|",
      "link",
      "image",
      "video",
      "table",
      // "iframeEditor",
      "|",
      "emoji",
      "symbols",
      "changeCase",
      "pasteCode",
      "|",
      "eraser",
      "copyformat",
      "|",
      "hr",
      "|",
      "showBlocks",
      "pageBreak",
      "|",
      "fullsize",
      "selectall",
      "print",
      "|",
      "find",
      "google",
      ...(translateEnabled ? ["translate"] : []),
      "|",
      "speechRecognize",
      "mobileView",
      // "restore" / "backup": en JODIT PRO existe plugin backup; en open source no hay botón restore.
      // "exportDocs", //se necesita backend para generar PDF/DOC, descomentar si se configura exportDocs.pdf.allow y opciones.
    ];

    return {
      license: LICENSE_KEY_EDITOR,
      loadExternalConfig: false,
      placeholder: placeholder || "Type or paste your content here!",
      language: lang === "es" ? "es" : "en",
      height: 400,
      width: "100%",

      // Plugins PRO: incluir aquí los que estén disponibles en tu build (fat/slim).
      // Ver https://xdsoft.net/jodit/pro/docs/plugin/
      extraPlugins: [
        "emoji",
        "todo-list",
        "change-case",
        "paste-code",
        "show-blocks",
        "page-break",
        ...(translateEnabled ? ["translate"] : []),
        "color-picker",
        "keyboard",
        "google-search",
        // "iframe-editor",
        // "googleMaps",   // Descomentar si se configura API_KEY (ver googleMaps más abajo).
        // "finder",       // Descomentar si se configura filebrowser.ajax.url (backend).
        // "autocomplete",  // Opcional: datos locales o backend.
        // "backup",
        // pasteFromWord,
        // "highlight-signature",
        // "paste-from-word",
        "mobile-view",
        // "tune-block",
        // "exportDocs",
      ],

      // toolbarAdaptive: true hace que JODIT use buttons / buttonsMD / buttonsSM / buttonsXS
      // según el ancho del editor. Si no definimos MD y SM, usa listas por defecto con muchos botones.
      // Definimos MD y SM igual que buttons para que solo haya dos variantes: lista completa o XS.
      buttons: mainToolbarButtons,
      buttonsMD: mainToolbarButtons,
      buttonsSM: mainToolbarButtons,
      buttonsXS: [
        "bold",
        "italic",
        "underline",
        "|",
        "ul",
        "ol",
        "todoList",
        "|",
        "link",
        "image",
        "emoji",
      ],
      removeButtons: [],
      showToolbar: true,
      showXPathInStatusbar: false,
      askBeforePasteHTML: false,
      askBeforePasteFromWord: false,
      defaultActionOnPaste: "insert_as_html",
      cleanHTML: {
        fillEmptyParagraph: true,
        removeEmptyElements: true,
      },
      uploader: {
        insertImageAsBase64URI: true,
      },
      image: {
        editSrc: true,
        editLink: true,
        editTitle: true,
        editAlt: true,
        editSize: true,
        editClass: true,
        editStyle: true,
        editId: true,
        editAlign: true,
        editMargin: true,
        editBorderRadius: true,
      },
      table: {
        insert: true,
        edit: true,
        remove: true,
        merge: true,
        split: true,
        rows: true,
        cols: true,
        cell: true,
        cellProperties: true,
        tableProperties: true,
      },
      link: {
        openOnClick: false,
        followLinkOnDblClick: false,
      },

      // --- Configuración de plugins PRO ---

      // Emoji: listado integrado; opcional data() para fuente remota o personalizada.
      emoji: {
        enableAutoComplete: true,
        recentCountLimit: 10,
      },

      // ToDo List: listas con casillas de verificación.
      todoList: {
        className: "todo-list",
        labelClassName: "todo-list__label",
      },

      // Translate: Jodit Cloud (Yandex). Ver cloudProviderOptions en la doc de Cloud.
      translate: translateEnabled
        ? {
            provider: "google",
            googleProviderOptions: {
              key: String(API_KEY_TRANSLATOR).trim(),
            },
            defaultSourceLang: "auto",
            defaultTargetLang: lang === "es" ? "es" : "en",
          }
        : {},

      // Google Maps: REQUIERE API key. Descomentar y definir API_KEY cuando se use.
      // Habilitar: Maps JavaScript API, Geocoding API, Maps Static API, Places API.
      // googleMaps: {
      //   API_KEY: process.env.REACT_APP_GOOGLE_MAPS_API_KEY || '',
      //   map: { type: 'roadmap', zoom: 10, size: [500, 400] },
      // },

      // Finder (filebrowser PRO): REQUIERE backend. Descomentar cuando exista URL de API.
      // filebrowser: {
      //   ajax: { url: 'https://tu-backend.com/finder/' },
      //   uploader: { url: 'https://tu-backend.com/finder/?action=fileUpload' },
      // },

      // Export PDF/DOC: opciones de estilo y página. Backend puede ser necesario para generar PDF.
      exportDocs: {
        css: "body { font-family: DejaVu Sans, sans-serif; }",
        pdf: {
          allow: true,
          options: {
            defaultFont: "helvetica",
            format: "A4",
            page_orientation: "portrait",
          },
        },
      },

      defaultLineHeight: 1.5,
      controls: {
        font: {
          list: {
            "Montserrat, Arial, sans-serif": "Montserrat",
            "Gotham, Arial, sans-serif": "Gotham",
          },
        },
        lineHeight: {
          list: [1, 1.15, 1.25, 1.5, 1.75, 2, 2.5, 3],
        },
      },
    };
  }, [placeholder, lang, API_KEY_TRANSLATOR]);

  // Limpiar la instancia del editor cuando el componente se desmonte.
  // Se difiere al siguiente tick para evitar que Jodit acceda a objetos ya
  // liberados (p. ej. waitForReady().promise) durante el unmount de React.
  useEffect(() => {
    let timeoutId;
    return () => {
      const editor = editorRef.current;
      if (!editor) return;
      if (editor.isDestructed === true) return;
      const runCleanup = () => {
        try {
          const inst = editorRef.current;
          if (!inst || inst.isDestructed === true) return;
          if (typeof inst.destruct === "function") {
            inst.destruct();
          }
        } catch (error) {
          console.warn(
            "Error al limpiar el editor (se ignora):",
            error?.message ?? error,
          );
        }
      };
      timeoutId = setTimeout(runCleanup, 0);
    };
  }, []);

  // Sincronizar contenido con el padre solo al perder foco.
  // No usar onChange para setBody: actualizar en cada tecla hace que el padre
  // re-renderice, cambie la prop value y Jodit vuelva a aplicar el valor,
  // lo que resetea el cursor al principio de la línea.
  const handleBlur = (content) => {
    setBody(content);
  };

  return (
    <div className="main-container">
      <div className="editor-container editor-container_classic-editor editor-container_include-fullscreen">
        <div className="editor-container__editor">
          <JoditEditor
            ref={editorRef}
            value={body}
            config={config}
            onBlur={(content) => handleBlur(content)}
            tabIndex={1}
          />
        </div>
      </div>
    </div>
  );
}
